import React, { useRef, useEffect, useState } from "react"
import { Box, Typography, useTheme, Popover } from "@mui/material"
import * as d3 from "d3"

const CategoricalBarGraph = ( {
    barData = [],
    xAxisValuesKey = "name",
    barValuesKey = "score",
    graphTitle = "Scores by Person",
    bgColor,
    xAxisName = "People",
    yAxisName = "Scores",
    hideLegend = false,
    showLabels = true
} ) => {

    const container = useRef()
    const svgRef = useRef()
    const [dimensions, setDimensions] = useState( null )
    const { palette } = useTheme()

    const [tooltipData, setTooltipData] = useState( null )
    const [tooltipPosition, setTooltipPosition] = useState( { top: 0, left: 0 } )

    useEffect( () => {
        const resize = () => {
            const { width = 0, height = 0 } = container.current?.getBoundingClientRect() || {}
            setDimensions( { width, height } )
        }
        resize()
        window.addEventListener( "resize", resize )
        return () => window.removeEventListener( "resize", resize )
    }, [] )

    const colorScale = d3.scaleOrdinal()
        .domain( barData.map( d => d[xAxisValuesKey] ) )
        .range( d3.schemeTableau10 )

    const handleMouseMove = ( event, item ) => {
        setTooltipData( item )
        setTooltipPosition( { top: event.pageY, left: event.pageX } )
    }

    const handleMouseLeave = () => {
        setTooltipData( null )
    }

    useEffect( () => {
        if ( !dimensions || barData.length === 0 ) return

        const { width, height } = dimensions
        const margin = { top: 30, right: 40, bottom: 80, left: 50 }

        const svg = d3.select( svgRef.current )
        svg.selectAll( "*" ).remove()

        const x = d3.scaleBand()
            .domain( barData.map( d => d[xAxisValuesKey] ) )
            .range( [margin.left, width - margin.right] )
            .padding( 0.6 ) // reduces width

        const y = d3.scaleLinear()
            .domain( [0, d3.max( barData, d => +d[barValuesKey] )] )
            .nice()
            .range( [height - margin.bottom, margin.top] )

        svg.append( "g" )
            .selectAll( "rect" )
            .data( barData )
            .enter()
            .append( "rect" )
            .attr( "x", d => x( d[xAxisValuesKey] ) )
            .attr( "y", d => y( d[barValuesKey] ) )
            .attr( "width", x.bandwidth() )
            .attr( "height", d => height - margin.bottom - y( d[barValuesKey] ) )
            .attr( "fill", d => colorScale( d[xAxisValuesKey] ) )
            .attr( "rx", 3 )
            .style( "cursor", "pointer" )
            .on( "mousemove", ( event, d ) => handleMouseMove( event, d ) )
            .on( "mouseleave", handleMouseLeave )

        svg.append( "g" )
            .attr( "transform", `translate(0,${height - margin.bottom})` )
            .call( d3.axisBottom( x ) )
            .selectAll( "text" )
            .attr( "transform", "rotate(-65)" )
            .style( "text-anchor", "end" )
            .attr( "dx", "-0.5em" )
            .attr( "dy", "0.1em" )

        svg.append( "g" )
            .attr( "transform", `translate(${margin.left},0)` )
            .call( d3.axisLeft( y ).ticks( 5 ) )

        if ( showLabels ) {
            svg.append( "text" )
                .attr( "text-anchor", "middle" )
                .attr( "x", width / 2 )
                .attr( "y", height - 25 )
                .text( xAxisName )
                .style( "font-size", "14px" )

            svg.append( "text" )
                .attr( "text-anchor", "middle" )
                .attr( "transform", "rotate(-90)" )
                .attr( "x", -height / 2 )
                .attr( "y", 10 )
                .text( yAxisName )
                .style( "font-size", "14px" )
        }

    }, [dimensions, barData] )

    return (
        <Box bgcolor={palette.contentBg}
            borderRadius="10px"
            minHeight="350px"
            display="flex"
            flexDirection="column"
            padding="0 10px 0 20px"
            flexGrow={1} >

            <Box display="flex" justifyContent="left" padding="10px">
                <Typography variant="subtitle2">{graphTitle}</Typography>
            </Box>

            <Box ref={container} flexGrow={1}>
                <svg ref={svgRef} width={dimensions?.width} height={dimensions?.height} className="graph-svg-container" />
            </Box>

            {/* Legend */}
            {!hideLegend && (
                <Box display="flex" justifyContent="center" gap="15px" padding="10px" flexWrap="wrap">
                    {barData.map( ( d ) => (
                        <Typography
                            key={d[xAxisValuesKey]}
                            variant="caption"  // reduced font size
                            fontWeight="bold"
                            fontSize="10px"
                            sx={{
                                "&::before": {
                                    content: '""',
                                    display: "inline-block",
                                    marginRight: "5px",
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "50%",
                                    background: colorScale( d[xAxisValuesKey] )
                                }
                            }}
                        >
                            {d[xAxisValuesKey]}
                        </Typography>
                    ) )}
                </Box>
            )}

            {/* Tooltip Popper */}
            <Popover
                open={Boolean( tooltipData )}
                anchorReference="anchorPosition"
                anchorPosition={tooltipPosition}
                sx={{ pointerEvents: "none" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box padding="10px">
                    {tooltipData && (
                        <>
                            <Typography variant="body2" fontWeight="bold">
                                {tooltipData[xAxisValuesKey]}
                            </Typography>
                            <Typography variant="caption">{yAxisName}: {tooltipData[barValuesKey]}</Typography>
                        </>
                    )}
                </Box>
            </Popover>
        </Box>
    )
}

export default CategoricalBarGraph
